import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Clock, MapPin, Phone, ShieldCheck, Siren } from 'lucide';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NbIcon } from '@nabotavle/ui/core';
import { EmergencyStore } from './emergency-store';

/**
 * The global resident takeover (prototypen i `docs/design`) — a full-screen breakthrough rendered
 * over the entire shell whenever there is an active alert. Pulsing siren, the alert title + body, the
 * beredskap oppmøteplass, a one-tap emergency call, and a "Jeg er i sikkerhet" acknowledgement. Driven
 * by `EmergencyStore.currentAlert`; clears live when the styre resolves (hub push → null).
 */
@Component({
  selector: 'nb-emergency-takeover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, NbIcon, TranslatePipe],
  template: `@let a = store.currentAlert();
    @if (a) {
      <div class="emerg" role="alertdialog" aria-modal="true" [attr.aria-label]="'beredskap.alert.aria' | translate: { title: a.title }">
        <div class="top">
          <div class="siren"><nb-icon [icon]="Siren" /></div>
          <div>
            <div class="over">{{ 'beredskap.alert.over' | translate: { type: typeLabel(a.type) } }}</div>
            <div class="display">{{ a.title }}</div>
            <p class="lead">{{ a.body }}</p>
          </div>
          <div class="cards">
            @if (store.assemblyPoint(); as ap) {
              <div class="info-card">
                <span class="ic"><nb-icon [icon]="MapPin" /></span>
                <div><div class="t">{{ 'beredskap.alert.assemblyPoint' | translate }}</div><div class="s">{{ ap.locationText }}</div></div>
              </div>
            }
            <div class="info-card">
              <span class="ic"><nb-icon [icon]="Clock" /></span>
              <div><div class="t">{{ 'beredskap.alert.sentAt' | translate: { time: (a.triggeredAtUtc | date: 'HH:mm') } }}</div><div class="s">{{ 'beredskap.alert.byBoard' | translate }}</div></div>
            </div>
          </div>
        </div>
        <div class="actions">
          <a class="btn white" [href]="'tel:' + emergencyNumber(a.type)"><nb-icon [icon]="Phone" /> {{ 'beredskap.alert.call' | translate: { number: emergencyNumber(a.type) } }}</a>
          @if (a.iAcked) {
            <div class="safe"><nb-icon [icon]="ShieldCheck" /> {{ 'beredskap.alert.safe' | translate }}</div>
          } @else {
            <button class="btn outline" type="button" (click)="store.acknowledge(a.id)">{{ 'beredskap.alert.imSafe' | translate }}</button>
          }
        </div>
      </div>
    }`,
  styles: `
    /* Full-screen breakthrough over the whole shell (prototype in docs/design). */
    .emerg { position: fixed; inset: 0; z-index: 1000; display: flex; flex-direction: column; background: linear-gradient(170deg, #8c4337, #6e2f26); color: #fff; text-align: center; padding-top: var(--safe-top); }
    .top { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 0 28px; gap: 18px; inline-size: 100%; max-inline-size: 640px; margin-inline: auto; } /* tablet (B5): keep the alert readable, not edge-to-edge */
    .siren { inline-size: 96px; block-size: 96px; border-radius: 50%; background: rgba(255, 255, 255, 0.14); display: flex; align-items: center; justify-content: center; animation: nb-pulse 1.4s ease-in-out infinite; --nb-icon-size: 48px; }
    @keyframes nb-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.3); } 50% { box-shadow: 0 0 0 22px rgba(255, 255, 255, 0); } }
    @media (prefers-reduced-motion: reduce) { .siren { animation: none; } }
    .over { color: rgba(255, 255, 255, 0.85); letter-spacing: 0.12em; text-transform: uppercase; font-size: 12px; font-weight: 700; }
    .display { font-family: var(--display); color: #fff; font-size: 27px; margin-top: 8px; line-height: 1.1; }
    .lead { color: rgba(255, 255, 255, 0.9); font-size: 16px; margin-top: 10px; line-height: 1.45; }
    .cards { inline-size: 100%; display: flex; flex-direction: column; gap: 10px; margin-top: 6px; }
    .info-card { background: rgba(255, 255, 255, 0.12); border-radius: var(--r-lg); padding: 14px; text-align: start; display: flex; gap: 12px; align-items: center; }
    .info-card .ic { inline-size: 42px; block-size: 42px; border-radius: 11px; background: rgba(255, 255, 255, 0.16); display: flex; align-items: center; justify-content: center; flex-shrink: 0; --nb-icon-size: 21px; }
    .info-card .t { font-weight: 700; font-size: 14.5px; }
    .info-card .s { font-size: 12.5px; color: rgba(255, 255, 255, 0.8); }
    .actions { inline-size: 100%; max-inline-size: 640px; margin-inline: auto; padding: 0 22px 30px; padding-bottom: calc(30px + var(--safe-bottom)); display: flex; flex-direction: column; gap: 11px; }
    .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: var(--r-lg); font-weight: 700; cursor: pointer; text-decoration: none; --nb-icon-size: 20px; }
    .btn:focus-visible { outline: 3px solid #fff; outline-offset: 2px; } /* the token ring is invisible on the red takeover */
    .btn.white { background: #fff; color: #7e3a30; block-size: 54px; font-size: 16px; border: none; }
    .btn.outline { background: transparent; border: 1.5px solid rgba(255, 255, 255, 0.5); color: #fff; block-size: 50px; font-size: 15px; }
    .safe { display: inline-flex; align-items: center; justify-content: center; gap: 8px; block-size: 50px; color: #fff; font-weight: 700; background: rgba(255, 255, 255, 0.12); border-radius: var(--r-lg); --nb-icon-size: 20px; }
  `,
})
export class EmergencyTakeover {
  protected readonly store = inject(EmergencyStore);
  private readonly t = inject(TranslateService);
  protected readonly Siren = Siren;
  protected readonly MapPin = MapPin;
  protected readonly Clock = Clock;
  protected readonly Phone = Phone;
  protected readonly ShieldCheck = ShieldCheck;

  /** Lower-case type word for the «Nødvarsel · {type}» overline; unknown types read as a plain message. */
  protected typeLabel(type: string): string {
    const known = ['Brann', 'Vannlekkasje', 'Evakuering', 'Annet'].includes(type);
    return this.t.instant(`beredskap.alert.type.${known ? type : 'Annet'}`);
  }

  /** Norwegian emergency line by type — 110 (brann) matches the mockup; 112 (politi) coordinates the rest. */
  protected emergencyNumber(type: string): string {
    return type === 'Brann' ? '110' : '112';
  }
}
